import { Link } from '@backstage/core-components';
import { alertApiRef, useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  DOC_FEEDBACK_CATEGORIES,
  DOC_FEEDBACK_CATEGORY_LABELS,
  DOC_FEEDBACK_LIMITS,
  DocFeedbackCategory,
} from '@internal/backstage-plugin-doc-feedback-common';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { FormEvent, useState } from 'react';
import { docFeedbackApiRef } from '../../api';
import { rootRouteRef } from '../../routes';

const CATEGORY_HINTS: Record<DocFeedbackCategory, string> = {
  question: 'Something is unclear and you need an answer',
  concern: 'Something looks risky, outdated or against policy',
  inaccuracy: 'Something on this page is wrong',
  suggestion: 'An idea to make this page better',
};

const useStyles = makeStyles(theme => ({
  legend: {
    display: 'block',
    marginBottom: theme.spacing(1.5),
  },
  categories: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(3),
  },
  category: {
    alignItems: 'flex-start',
    margin: 0,
    '& .MuiRadio-root': {
      paddingTop: theme.spacing(0.5),
    },
  },
  footer: {
    flex: 1,
    paddingLeft: theme.spacing(2),
  },
  hint: {
    color: theme.palette.text.secondary,
  },
  quote: {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    background: theme.palette.action.hover,
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
    whiteSpace: 'pre-wrap',
    maxHeight: 160,
    overflow: 'auto',
  },
  page: {
    marginBottom: theme.spacing(2),
  },
}));

/** @public */
export type FeedbackDialogProps = {
  open: boolean;
  onClose: () => void;
  entityRef: string;
  pagePath: string;
  pageTitle?: string;
  selectedText?: string;
  initialCategory?: DocFeedbackCategory;
};

export const FeedbackDialog = ({
  open,
  onClose,
  entityRef,
  pagePath,
  pageTitle,
  selectedText,
  initialCategory,
}: FeedbackDialogProps) => {
  const classes = useStyles();
  const api = useApi(docFeedbackApiRef);
  const alertApi = useApi(alertApiRef);
  const feedbackRoute = useRouteRef(rootRouteRef);
  const [category, setCategory] = useState<DocFeedbackCategory>(initialCategory ?? 'question');
  const [message, setMessage] = useState('');
  const [quote, setQuote] = useState(selectedText);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  const trimmed = message.trim();
  const tooLong = message.length > DOC_FEEDBACK_LIMITS.message;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!trimmed || tooLong) {
      return;
    }
    setSubmitting(true);
    setError(undefined);
    try {
      await api.submitFeedback({
        entityRef,
        pagePath,
        pageTitle,
        category,
        message: trimmed,
        selectedText: quote,
      });
      alertApi.post({
        message:
          'Thanks! Your feedback was sent to the team that owns this doc. Follow it up under Doc Feedback > My submissions.',
        severity: 'success',
        display: 'transient',
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="doc-feedback-dialog-title"
    >
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle id="doc-feedback-dialog-title">Give feedback on this page</DialogTitle>
        <DialogContent>
          <Typography variant="body2" className={classes.page}>
            {pageTitle ? <strong>{pageTitle}</strong> : null}
            {pageTitle ? ' · ' : null}
            <span className={classes.hint}>{entityRef}</span>
          </Typography>

          <FormLabel component="legend" className={classes.legend}>
            What kind of feedback?
          </FormLabel>
          <RadioGroup
            aria-label="Feedback category"
            name="category"
            value={category}
            onChange={e => setCategory(e.target.value as DocFeedbackCategory)}
            className={classes.categories}
          >
            {DOC_FEEDBACK_CATEGORIES.map(c => (
              <FormControlLabel
                key={c}
                value={c}
                className={classes.category}
                control={<Radio color="primary" />}
                label={
                  <>
                    <Typography variant="body2">{DOC_FEEDBACK_CATEGORY_LABELS[c]}</Typography>
                    <Typography variant="caption" className={classes.hint}>
                      {CATEGORY_HINTS[c]}
                    </Typography>
                  </>
                }
              />
            ))}
          </RadioGroup>

          {quote ? (
            <>
              <Typography variant="caption" className={classes.hint}>
                About the highlighted text
              </Typography>
              <Typography
                variant="body2"
                component="blockquote"
                className={classes.quote}
                data-testid="doc-feedback-quote"
              >
                {quote}
              </Typography>
              <Button size="small" onClick={() => setQuote(undefined)}>
                Remove highlighted text
              </Button>
            </>
          ) : (
            <Typography variant="caption" className={classes.hint}>
              Tip: highlight text on the page before clicking "Give feedback" to quote it.
            </Typography>
          )}

          <TextField
            id="doc-feedback-message"
            label="Your feedback"
            placeholder="What should we know? Be as specific as you can."
            multiline
            minRows={4}
            fullWidth
            required
            autoFocus
            margin="normal"
            variant="outlined"
            value={message}
            onChange={e => setMessage(e.target.value)}
            error={tooLong}
            helperText={
              tooLong
                ? `Please keep it under ${DOC_FEEDBACK_LIMITS.message} characters`
                : 'Your name is shared with the doc owners so they can follow up.'
            }
          />
          {error && <Alert severity="error">{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Typography variant="body2" className={classes.footer}>
            <Link to={`${feedbackRoute()}?tab=mine`}>Your feedback and replies</Link>
          </Typography>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={!trimmed || tooLong || submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : undefined}
          >
            Send feedback
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
